import { useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-hot-toast";
import { HiCheck } from "react-icons/hi";

import API from "../api";
import { capture } from "../instrument";
import { PASSWORD_MIN_LENGTH, hasPasswordLength, hasPasswordVariety, isPasswordStrong } from "../utils";

const ChangePassword = () => {
  const history = useHistory();
  const [form, setForm] = useState({ currentPassword: "", newPassword: "", newPasswordConfirm: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canSubmit = form.currentPassword.length > 0 && isPasswordStrong(form.newPassword) && form.newPassword === form.newPasswordConfirm;

  const changePassword = async () => {
    setIsSubmitting(true);
    try {
      const res = await API.put("/user/password", { currentPassword: form.currentPassword, newPassword: form.newPassword });
      if (!res.ok) throw res;
      toast.success("Mot de passe modifié");
      history.push("/");
    } catch (error) {
      capture(error);
      toast.error(error.message || "Une erreur est survenue");
    }
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-primary-500">
      <div className="mx-auto flex min-h-screen max-w-5xl flex-col justify-center px-4 py-12 sm:px-6 lg:px-12">
        <div className="grid gap-10 rounded-[28px] border border-slate-200 bg-white p-8 shadow-sm md:grid-cols-[1.1fr_1fr] md:p-12">
          <section className="flex flex-col justify-between gap-12">
            <div className="flex h-full flex-col justify-center space-y-4">
              <p className="text-xs font-medium uppercase tracking-[0.3em] text-slate-400">Querco</p>
              <h1 className="text-2xl font-semibold text-primary-500">Choisissez votre mot de passe</h1>
              <p className="text-sm text-slate-500">
                Le mot de passe qui vous a été remis est provisoire. Choisissez-en un nouveau, connu de vous seul, pour accéder à votre espace.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400">
              <span>&copy; {new Date().getFullYear()} Querco</span>
              <span className="hidden h-3 w-px bg-slate-200 sm:inline" />
              <span>Support: support@querco.co</span>
            </div>
          </section>

          <section className="border-t border-slate-100 pt-8 md:border-t-0 md:pl-8 md:pt-0">
            <form
              className="space-y-6"
              onSubmit={(e) => {
                e.preventDefault();
                if (canSubmit && !isSubmitting) changePassword();
              }}
            >
              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500" htmlFor="current-password">
                  Mot de passe provisoire
                </label>
                <input
                  id="current-password"
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10"
                  type="password"
                  autoFocus
                  autoComplete="current-password"
                  value={form.currentPassword}
                  onChange={(e) => setForm({ ...form, currentPassword: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500" htmlFor="new-password">
                  Nouveau mot de passe
                </label>
                <input
                  id="new-password"
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10"
                  type="password"
                  autoComplete="new-password"
                  value={form.newPassword}
                  onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
                />
                <ul className="space-y-1 text-xs">
                  <li className={`flex items-center gap-1.5 ${hasPasswordLength(form.newPassword) ? "text-slate-700" : "text-slate-400"}`}>
                    <HiCheck className="size-3.5 shrink-0" />
                    {PASSWORD_MIN_LENGTH} caractères minimum
                  </li>
                  <li className={`flex items-center gap-1.5 ${hasPasswordVariety(form.newPassword) ? "text-slate-700" : "text-slate-400"}`}>
                    <HiCheck className="size-3.5 shrink-0" />
                    Trois types de caractères parmi minuscules, majuscules, chiffres et spéciaux
                  </li>
                </ul>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500" htmlFor="new-password-confirm">
                  Confirmation
                </label>
                <input
                  id="new-password-confirm"
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10"
                  type="password"
                  autoComplete="new-password"
                  value={form.newPasswordConfirm}
                  onChange={(e) => setForm({ ...form, newPasswordConfirm: e.target.value })}
                />
                {form.newPasswordConfirm.length > 0 && form.newPassword !== form.newPasswordConfirm && (
                  <p className="text-xs text-slate-500">Les deux mots de passe ne correspondent pas.</p>
                )}
              </div>

              <button
                type="submit"
                disabled={!canSubmit || isSubmitting}
                className="flex w-full items-center justify-center rounded-xl bg-primary-500 px-4 py-3 text-sm font-semibold text-white transition hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-white disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                Enregistrer et continuer
              </button>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
